
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from "firebase/firestore";
import { db, IS_MOCK_MODE } from "./firebase";
import { Project, WbsItem, LogEntry } from '../types';
import { MOCK_PROJECTS, MOCK_LOGS, MOCK_WBS } from '../constants';

const CLOUDFLARE_BASE_URL = import.meta.env.VITE_BACKEND_URL;
const USE_CLOUDFLARE = !!CLOUDFLARE_BASE_URL;

/**
 * Production Persistence Layer
 * Interfaces with Cloudflare D1 (if VITE_BACKEND_URL is set) or Firestore.
 * Falls back to local mocks if no cloud service is configured.
 */
export class PersistenceManager {

  static async getProjects(): Promise<Project[]> {
    console.log("MarginGuard: Fetching projects. Base URL:", CLOUDFLARE_BASE_URL);
    if (USE_CLOUDFLARE) {
      console.log("MarginGuard: Using Cloudflare Backend");
      try {
        const res = await fetch(`${CLOUDFLARE_BASE_URL}/projects`);
        if (!res.ok) throw new Error("Cloudflare error");
        return await res.json();
      } catch (e) {
        console.warn("Cloudflare retrieval failed, falling back to mock");
        return MOCK_PROJECTS;
      }
    }

    if (IS_MOCK_MODE || !db) {
      if (IS_MOCK_MODE) console.warn("MarginGuard: Operating in MOCK MODE.");
      return MOCK_PROJECTS;
    }
    try {
      const colRef = collection(db, "projects");
      const snapshot = await getDocs(colRef);
      if (snapshot.empty) return MOCK_PROJECTS;
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Project));
    } catch (e) {
      console.error("Firestore retrieval error", e);
      return MOCK_PROJECTS;
    }
  }

  static async getProjectLogs(projectId: string): Promise<LogEntry[]> {
    if (USE_CLOUDFLARE) {
      try {
        const res = await fetch(`${CLOUDFLARE_BASE_URL}/projects/${projectId}/logs`);
        return await res.json();
      } catch (e) {
        return MOCK_LOGS.filter(l => l.projectId === projectId);
      }
    }

    if (IS_MOCK_MODE || !db) return MOCK_LOGS.filter(l => l.projectId === projectId);
    try {
      const colRef = collection(db, "projects", projectId, "logs");
      const q = query(colRef, orderBy("date", "desc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as LogEntry));
    } catch (e) {
      return MOCK_LOGS.filter(l => l.projectId === projectId);
    }
  }

  static async getProjectWbs(projectId: string): Promise<WbsItem[]> {
    if (USE_CLOUDFLARE) {
      try {
        const res = await fetch(`${CLOUDFLARE_BASE_URL}/projects/${projectId}/wbs`);
        return await res.json();
      } catch (e) {
        return MOCK_WBS.filter(w => w.projectId === projectId);
      }
    }

    if (IS_MOCK_MODE || !db) return MOCK_WBS.filter(w => w.projectId === projectId);
    try {
      const colRef = collection(db, "projects", projectId, "wbs");
      const snapshot = await getDocs(colRef);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as WbsItem));
    } catch (e) {
      return MOCK_WBS.filter(w => w.projectId === projectId);
    }
  }

  static async addLog(projectId: string, log: Omit<LogEntry, 'id'>): Promise<string> {
    if (USE_CLOUDFLARE) {
      const res = await fetch(`${CLOUDFLARE_BASE_URL}/projects/${projectId}/logs`, {
        method: "POST",
        body: JSON.stringify(log),
        headers: { "Content-Type": "application/json" }
      });
      const data = await res.json();
      return data.id;
    }

    if (IS_MOCK_MODE || !db) return `mock-${Date.now()}`;
    const colRef = collection(db, "projects", projectId, "logs");
    const docRef = await addDoc(colRef, {
      ...log,
      createdAt: Timestamp.now()
    });
    return docRef.id;
  }

  static async updateLog(projectId: string, logId: string, updates: Partial<LogEntry>): Promise<void> {
    if (USE_CLOUDFLARE) {
      await fetch(`${CLOUDFLARE_BASE_URL}/projects/${projectId}/logs/${logId}`, {
        method: "PUT",
        body: JSON.stringify(updates),
        headers: { "Content-Type": "application/json" }
      });
      return;
    }

    if (IS_MOCK_MODE || !db) return;
    const docRef = doc(db, "projects", projectId, "logs", logId);
    await updateDoc(docRef, updates);
  }

  static async saveChangeOrder(projectId: string, co: any): Promise<string> {
    if (USE_CLOUDFLARE) {
      const res = await fetch(`${CLOUDFLARE_BASE_URL}/projects/${projectId}/change-orders`, {
        method: "POST",
        body: JSON.stringify(co),
        headers: { "Content-Type": "application/json" }
      });
      const data = await res.json();
      return data.id;
    }

    if (IS_MOCK_MODE || !db) return `mock-co-${Date.now()}`;
    const colRef = collection(db, "projects", projectId, "changeOrders");
    const docRef = await addDoc(colRef, {
      ...co,
      createdAt: Timestamp.now(),
      status: "Draft"
    });
    return docRef.id;
  }
}
